/* eslint-disable react/no-unescaped-entities */
/* Bol Karlilik Analizi — Urun bazli net kar + trend. */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
    TrendingUp, TrendingDown, Minus, Loader2, AlertCircle,
    Wallet, Crown, AlertTriangle, Package,
} from 'lucide-react';

const fmt = (n) => {
    if (typeof n !== 'number') return '0,00';
    return n.toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

function Trend({ t }) {
    if (t === 'up') return <span className="inline-flex items-center gap-1 text-emerald-700 text-[11px] font-semibold"><TrendingUp size={12} /> Yükseliş</span>;
    if (t === 'down') return <span className="inline-flex items-center gap-1 text-rose-700 text-[11px] font-semibold"><TrendingDown size={12} /> Düşüş</span>;
    return <span className="inline-flex items-center gap-1 text-gray-500 text-[11px]"><Minus size={12} /> Stabil</span>;
}

function KpiCard({ icon: Icon, label, value, sub, tone = 'gray', testId }) {
    const toneMap = {
        emerald: 'bg-emerald-50 border-emerald-200 text-emerald-900',
        rose: 'bg-rose-50 border-rose-200 text-rose-900',
        violet: 'bg-violet-50 border-violet-200 text-violet-900',
        amber: 'bg-amber-50 border-amber-200 text-amber-900',
        gray: 'bg-gray-50 border-gray-200 text-gray-900',
    };
    return (
        <div className={`border rounded-lg p-3 ${toneMap[tone]}`} data-testid={testId}>
            <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider opacity-80">
                <Icon size={14} /> {label}
            </div>
            <div className="text-xl font-bold mt-1.5">{value}</div>
            {sub && <div className="text-[11px] opacity-70 mt-0.5">{sub}</div>}
        </div>
    );
}

export default function BolProfitability() {
    const API = process.env.REACT_APP_BACKEND_URL;
    const token = localStorage.getItem('token');
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [sortBy, setSortBy] = useState('net_profit_desc');
    const [editingCost, setEditingCost] = useState({});  // ean -> tmpVal
    const [period, setPeriod] = useState('30d');  // '30d' | 'all'

    const load = (p = period) => {
        setLoading(true);
        axios.get(`${API}/api/bol/profitability?period=${p}`, { headers: { Authorization: `Bearer ${token}` } })
            .then((r) => setData(r.data))
            .catch(() => { /* ignore */ })
            .finally(() => setLoading(false));
    };
    useEffect(() => { load(period); /* eslint-disable-next-line */ }, [period]);

    const saveCost = async (ean) => {
        const newVal = parseFloat(editingCost[ean]);
        if (Number.isNaN(newVal) || newVal < 0) { return; }
        try {
            await axios.put(`${API}/api/bol/product-cost`,
                { ean, cost: newVal },
                { headers: { Authorization: `Bearer ${token}` } });
            setEditingCost((p) => { const c = { ...p }; delete c[ean]; return c; });
            load(period);
        } catch { /* ignore */ }
    };

    if (loading) return <div className="flex items-center justify-center py-16"><Loader2 className="animate-spin text-gray-400" size={24} /></div>;
    if (!data || !data.products?.length) {
        return <div className="text-center py-12 text-sm text-gray-500" data-testid="bol-profitability-empty">
            Veri yok. Bol.com sync yapın.
        </div>;
    }

    const s = data.summary;
    let products = [...data.products];
    if (sortBy === 'net_profit_desc') products.sort((a, b) => b.net_profit - a.net_profit);
    else if (sortBy === 'net_profit_asc') products.sort((a, b) => a.net_profit - b.net_profit);
    else if (sortBy === 'qty_desc') products.sort((a, b) => b.qty_30d - a.qty_30d);
    else if (sortBy === 'margin_desc') products.sort((a, b) => b.margin_pct - a.margin_pct);
    else if (sortBy === 'margin_asc') products.sort((a, b) => a.margin_pct - b.margin_pct);

    return (
        <div className="space-y-4" data-testid="bol-profitability">
            {/* Periyot toggle */}
            <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="flex items-center gap-1 bg-gray-100 p-1 rounded-md" data-testid="prof-period-toggle">
                    <button onClick={() => setPeriod('30d')}
                        className={`px-3 py-1.5 text-xs font-semibold rounded ${period === '30d' ? 'bg-white text-gray-900 shadow' : 'text-gray-500 hover:text-gray-700'}`}
                        data-testid="prof-period-30d">
                        📅 Son 30 gün
                    </button>
                    <button onClick={() => setPeriod('all')}
                        className={`px-3 py-1.5 text-xs font-semibold rounded ${period === 'all' ? 'bg-white text-gray-900 shadow' : 'text-gray-500 hover:text-gray-700'}`}
                        data-testid="prof-period-all">
                        🗓 Tüm zamanlar
                    </button>
                </div>
                <span className="text-[11px] text-gray-500">
                    {period === '30d'
                        ? 'Son 30 günlük siparişler — ciro, komisyon, kargo, reklam, net kâr'
                        : 'Tüm zamanlar — iptal hariç tüm siparişler'}
                </span>
            </div>

            {/* Ozet KPI */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <KpiCard icon={Wallet} label="Toplam Net Kâr" tone="emerald"
                    value={`${fmt(s.total_net_profit)} €`}
                    sub={`Net ciro ${fmt(s.total_revenue_excl_vat)} €`}
                    testId="prof-total-net" />
                <KpiCard icon={TrendingUp} label="Ortalama Marj" tone="emerald"
                    value={`%${fmt(s.avg_margin_pct)}`}
                    sub="net / net ciro"
                    testId="prof-avg-margin" />
                <KpiCard icon={Crown} label="En Karlı Ürün" tone="violet"
                    value={`${fmt(s.best_profit)} €`}
                    sub={(s.best_title || '').slice(0, 35) + ((s.best_title || '').length > 35 ? '...' : '')}
                    testId="prof-best" />
                <KpiCard icon={s.products_in_loss > 0 ? AlertTriangle : Package}
                    label="Zarar Eden Ürün"
                    tone={s.products_in_loss > 0 ? 'rose' : 'gray'}
                    value={s.products_in_loss}
                    sub={s.products_in_loss > 0 ? 'fiyat/maliyeti gözden geçir' : 'hepsi karlı 🎉'}
                    testId="prof-in-loss" />
            </div>

            {/* Reklam dagitim notu */}
            {s.total_ad_spent === 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-2.5 text-[11px] text-amber-900 flex items-start gap-2">
                    <AlertCircle size={14} className="shrink-0 mt-0.5" />
                    <div>
                        Reklam maliyeti çekilmiyor (Advertising client kayıtlı değil veya 0). Karlılık hesabı bu yüzden olduğundan biraz daha yüksek görünüyor.
                    </div>
                </div>
            )}

            {/* Sirala */}
            <div className="flex items-center gap-2">
                <span className="text-xs text-gray-600">Sırala:</span>
                <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}
                    className="h-8 px-2 text-xs border border-gray-300 rounded-md bg-white"
                    data-testid="prof-sort">
                    <option value="net_profit_desc">En karlıdan az karlıya</option>
                    <option value="net_profit_asc">En zararlıdan karlıya</option>
                    <option value="qty_desc">En çok satılan</option>
                    <option value="margin_desc">En yüksek marj %</option>
                    <option value="margin_asc">En düşük marj %</option>
                </select>
                <span className="ml-auto text-xs text-gray-500">{products.length} ürün</span>
            </div>

            {/* Tablo - Desktop */}
            <div className="hidden md:block bg-white border border-gray-200 rounded-lg overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b border-gray-200">
                        <tr className="text-[11px] uppercase tracking-wider text-gray-600 font-semibold">
                            <th className="px-3 py-2 text-left">Ürün</th>
                            <th className="px-3 py-2 text-right">30g Satış</th>
                            <th className="px-3 py-2 text-right">Ciro (BTW haric)</th>
                            <th className="px-3 py-2 text-right">Maliyet/Adet</th>
                            <th className="px-3 py-2 text-right">Toplam Maliyet</th>
                            <th className="px-3 py-2 text-right">Komisyon</th>
                            <th className="px-3 py-2 text-right">Kargo</th>
                            <th className="px-3 py-2 text-right">Reklam</th>
                            <th className="px-3 py-2 text-right">Net Kâr</th>
                            <th className="px-3 py-2 text-right">Marj</th>
                            <th className="px-3 py-2 text-center">Trend</th>
                        </tr>
                    </thead>
                    <tbody>
                        {products.map((p) => {
                            const isLoss = p.net_profit < 0;
                            const isLowMargin = p.margin_pct < 20 && !isLoss;
                            const isCostMissing = (p.cost_per_unit || 0) <= 0;
                            const editing = editingCost[p.ean] !== undefined;
                            return (
                                <tr key={p.ean}
                                    className={`border-b border-gray-100 hover:bg-gray-50 ${isLoss ? 'bg-rose-50/40' : ''}`}
                                    data-testid={`prof-row-${p.ean}`}>
                                    <td className="px-3 py-2">
                                        <div className="font-medium text-gray-900 leading-tight">{p.title}</div>
                                        <div className="text-[10px] text-gray-500 font-mono">EAN {p.ean}</div>
                                    </td>
                                    <td className="px-3 py-2 text-right font-semibold tabular-nums">
                                        {p.qty_30d}
                                        <div className="text-[10px] text-gray-500">7g: {p.qty_7d} ({p.qty_prev_7d})</div>
                                    </td>
                                    <td className="px-3 py-2 text-right tabular-nums">{fmt(p.revenue_excl_vat)} €</td>
                                    <td className="px-3 py-2 text-right">
                                        {editing ? (
                                            <div className="flex items-center gap-1 justify-end">
                                                <input type="number" step="0.01" autoFocus
                                                    value={editingCost[p.ean]}
                                                    onChange={(e) => setEditingCost((prev) => ({ ...prev, [p.ean]: e.target.value }))}
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter') saveCost(p.ean);
                                                        if (e.key === 'Escape') setEditingCost((prev) => { const c = { ...prev }; delete c[p.ean]; return c; });
                                                    }}
                                                    className="w-20 h-7 px-2 text-xs border border-blue-400 rounded text-right tabular-nums"
                                                    data-testid={`prof-cost-input-${p.ean}`} />
                                                <button onClick={() => saveCost(p.ean)} className="text-emerald-600 text-xs font-bold" data-testid={`prof-cost-save-${p.ean}`}>✓</button>
                                            </div>
                                        ) : (
                                            <button onClick={() => setEditingCost((prev) => ({ ...prev, [p.ean]: p.cost_per_unit || '' }))}
                                                className={`text-xs font-medium ${isCostMissing ? 'text-amber-700 underline decoration-dotted' : 'text-gray-700 hover:text-blue-600'} tabular-nums`}
                                                data-testid={`prof-cost-edit-${p.ean}`}
                                                title={p.cost_source === 'product' ? 'Urunler sayfasindan otomatik' : (p.cost_source === 'manual' ? 'Manuel girildi' : 'Maliyet girilmedi')}>
                                                {isCostMissing ? '— gir' : `${fmt(p.cost_per_unit)} €`}
                                            </button>
                                        )}
                                    </td>
                                    <td className="px-3 py-2 text-right tabular-nums text-rose-700">
                                        {p.cogs_total > 0 ? `−${fmt(p.cogs_total)} €` : '—'}
                                    </td>
                                    <td className="px-3 py-2 text-right tabular-nums text-rose-700">
                                        −{fmt(p.commission)} €
                                        {p.commission_source === 'actual' && (
                                            <span title="Bol fatura (gerçek)" className="ml-1 text-[9px] font-bold text-emerald-600">✓</span>
                                        )}
                                        {p.commission_source === 'mixed' && (
                                            <span title="Kısmen gerçek, kısmen tahmini" className="ml-1 text-[9px] font-bold text-amber-600">~</span>
                                        )}
                                    </td>
                                    <td className="px-3 py-2 text-right tabular-nums text-rose-700">
                                        −{fmt(p.transport)} €
                                        {p.transport_source === 'actual' && (
                                            <span title="Bol fatura (gerçek)" className="ml-1 text-[9px] font-bold text-emerald-600">✓</span>
                                        )}
                                        {p.transport_source === 'mixed' && (
                                            <span title="Kısmen gerçek, kısmen tahmini" className="ml-1 text-[9px] font-bold text-amber-600">~</span>
                                        )}
                                    </td>
                                    <td className="px-3 py-2 text-right tabular-nums text-rose-700">−{fmt(p.ad_spent)} €</td>
                                    <td className={`px-3 py-2 text-right tabular-nums font-bold ${isLoss ? 'text-rose-700' : 'text-emerald-700'}`}>
                                        {isLoss ? '' : '+'}{fmt(p.net_profit)} €
                                    </td>
                                    <td className={`px-3 py-2 text-right tabular-nums font-semibold ${isLoss ? 'text-rose-700' : isLowMargin ? 'text-amber-700' : 'text-emerald-700'}`}>
                                        %{fmt(p.margin_pct)}
                                    </td>
                                    <td className="px-3 py-2 text-center"><Trend t={p.trend} /></td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {/* Mobil Card View */}
            <div className="md:hidden space-y-2" data-testid="prof-mobile-list">
                {products.map((p) => {
                    const isLoss = p.net_profit < 0;
                    const isLowMargin = p.margin_pct < 20 && !isLoss;
                    const isCostMissing = (p.cost_per_unit || 0) <= 0;
                    const editing = editingCost[p.ean] !== undefined;
                    return (
                        <div key={p.ean}
                            className={`bg-white border rounded-lg p-3 ${isLoss ? 'border-rose-200 bg-rose-50/40' : 'border-gray-200'}`}
                            data-testid={`prof-card-${p.ean}`}>
                            {/* Üst: başlık + net kâr */}
                            <div className="flex items-start justify-between gap-2 mb-2">
                                <div className="min-w-0 flex-1">
                                    <div className="text-sm font-semibold text-gray-900 leading-tight line-clamp-2">{p.title}</div>
                                    <div className="text-[10px] text-gray-500 font-mono mt-0.5">EAN {p.ean}</div>
                                </div>
                                <div className="text-right shrink-0">
                                    <div className={`text-base font-bold tabular-nums ${isLoss ? 'text-rose-700' : 'text-emerald-700'}`}>
                                        {isLoss ? '' : '+'}{fmt(p.net_profit)} €
                                    </div>
                                    <div className={`text-[11px] font-semibold ${isLoss ? 'text-rose-700' : isLowMargin ? 'text-amber-700' : 'text-emerald-700'}`}>
                                        %{fmt(p.margin_pct)} marj
                                    </div>
                                </div>
                            </div>

                            {/* Satış + trend */}
                            <div className="flex items-center justify-between text-[11px] mb-2 pb-2 border-b border-gray-100">
                                <div className="text-gray-600">
                                    <span className="font-semibold text-gray-900">{p.qty_30d}</span> sat. (30g) · 7g: {p.qty_7d} ({p.qty_prev_7d})
                                </div>
                                <Trend t={p.trend} />
                            </div>

                            {/* Gider grid */}
                            <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-[11px]">
                                <div className="flex justify-between">
                                    <span className="text-gray-500">Ciro (BTW haric):</span>
                                    <span className="font-medium tabular-nums">{fmt(p.revenue_excl_vat)} €</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-500">Maliyet:</span>
                                    <span className="tabular-nums text-rose-700">{p.cogs_total > 0 ? `−${fmt(p.cogs_total)} €` : '—'}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-500">Komisyon:</span>
                                    <span className="tabular-nums text-rose-700">−{fmt(p.commission)} €</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-500">Kargo:</span>
                                    <span className="tabular-nums text-rose-700">−{fmt(p.transport)} €</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-500">Reklam:</span>
                                    <span className="tabular-nums text-rose-700">−{fmt(p.ad_spent)} €</span>
                                </div>
                                <div className="flex justify-between items-center">
                                    <span className="text-gray-500">Maliyet/ad:</span>
                                    {editing ? (
                                        <div className="flex items-center gap-1">
                                            <input type="number" step="0.01" autoFocus
                                                value={editingCost[p.ean]}
                                                onChange={(e) => setEditingCost((prev) => ({ ...prev, [p.ean]: e.target.value }))}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') saveCost(p.ean);
                                                    if (e.key === 'Escape') setEditingCost((prev) => { const c = { ...prev }; delete c[p.ean]; return c; });
                                                }}
                                                className="w-16 h-6 px-1.5 text-[11px] border border-blue-400 rounded text-right tabular-nums"
                                                data-testid={`prof-cost-input-m-${p.ean}`} />
                                            <button onClick={() => saveCost(p.ean)} className="text-emerald-600 text-xs font-bold" data-testid={`prof-cost-save-m-${p.ean}`}>✓</button>
                                        </div>
                                    ) : (
                                        <button onClick={() => setEditingCost((prev) => ({ ...prev, [p.ean]: p.cost_per_unit || '' }))}
                                            className={`text-[11px] font-medium ${isCostMissing ? 'text-amber-700 underline decoration-dotted' : 'text-gray-700'} tabular-nums`}
                                            data-testid={`prof-cost-edit-m-${p.ean}`}>
                                            {isCostMissing ? '— gir' : `${fmt(p.cost_per_unit)} €`}
                                        </button>
                                    )}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
            <div className="text-[11px] text-gray-500 px-1">
                <b>Hesap:</b> Net Kâr = Ciro (BTW hariç) − <b>Ürün maliyeti</b> − Bol komisyonu − Kargo (6,90 €/gönderim) − Reklam payı (ciro oranı kadar).
                Trend: son 7 gün vs önceki 7 gün satış.
                Maliyet girmek için kolondaki <b>"— gir"</b> butonuna basın (otomatik kaydedilir).
                Eğer Ürünler sayfasında EAN'a denk gelen `barcode` + `purchase_price` varsa otomatik gelir.
            </div>
        </div>
    );
}
